import { formatDateNoTime, InboundLeadForwardingRequestId } from "@sp-crm/core";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import React, { useCallback, useState } from "react";
import { getSanitizedMarkUpFromForwardingConfirmation } from "util/email";

export interface LeadSourceForwardingConfirmation {
    id: InboundLeadForwardingRequestId;
    providerName?: string | null;
    requesterAddress: string;
    receivedAt: string;
    bodyHtml: string;
}

export type ForwardingActionState =
    | { kind: "idle" }
    | { kind: "running"; id: InboundLeadForwardingRequestId }
    | { kind: "failed"; id: InboundLeadForwardingRequestId };

const IDLE: ForwardingActionState = { kind: "idle" };

export const isRunningFor = (
    state: ForwardingActionState,
    id: InboundLeadForwardingRequestId,
): boolean => state.kind === "running" && state.id === id;

export const isFailedFor = (
    state: ForwardingActionState,
    id: InboundLeadForwardingRequestId,
): boolean => state.kind === "failed" && state.id === id;

export interface ForwardingAction<T extends LeadSourceForwardingConfirmation> {
    state: ForwardingActionState;
    run: (request: T) => Promise<void>;
}

export const useForwardingAction = <T extends LeadSourceForwardingConfirmation>(
    perform: (request: T) => Promise<void>,
    onSucceeded: (request: T) => void,
): ForwardingAction<T> => {
    const [state, setState] = useState<ForwardingActionState>(IDLE);

    const run = useCallback(
        async (request: T) => {
            setState({ kind: "running", id: request.id });
            try {
                await perform(request);
            } catch {
                setState({ kind: "failed", id: request.id });
                return;
            }
            setState(IDLE);
            onSucceeded(request);
        },
        [onSucceeded, perform],
    );

    return { state, run };
};

export const describeForwardingAsk = (
    request: LeadSourceForwardingConfirmation,
): string =>
    request.providerName
        ? `asked ${request.providerName} to forward mail to this address.`
        : "asked to forward mail to this address.";

export const confirmationTitle = (request: LeadSourceForwardingConfirmation): string =>
    request.providerName
        ? `${request.providerName} forwarding confirmation`
        : "Forwarding confirmation";

interface ActionFailureMessageProps {
    message: string;
}

export const ActionFailureMessage: React.FC<ActionFailureMessageProps> = props => (
    <p className="text-sm text-red-600">{props.message}</p>
);

interface LeadSourceForwardingConfirmationPanelProps {
    request: LeadSourceForwardingConfirmation | null;
    banner: React.ReactNode;
    action: React.ReactNode;
    failure: React.ReactNode;
    onDismiss: () => void;
}

export const LeadSourceForwardingConfirmationPanel: React.FC<
    LeadSourceForwardingConfirmationPanelProps
> = props => {
    const { request, banner, action, failure, onDismiss } = props;

    return (
        <Panel
            isOpen={request !== null}
            type={PanelType.large}
            headerText={request ? confirmationTitle(request) : ""}
            onDismiss={onDismiss}>
            {request ? (
                <div className="space-y-4">
                    {banner}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-gray-500">
                            Received {formatDateNoTime(request.receivedAt)}
                        </p>
                        {action}
                    </div>
                    {failure}
                    <div
                        className="unreset message-body text-sm text-gray-700"
                        dangerouslySetInnerHTML={getSanitizedMarkUpFromForwardingConfirmation(
                            request.bodyHtml,
                        )}
                    />
                </div>
            ) : null}
        </Panel>
    );
};
